import { useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Building2, Plus, Trash2 } from 'lucide-react';
import type { Site, ExistingSupplierMatch } from '../../../lib/api';
import { CURRENCY_OPTIONS, getAnnualSpendOptions } from './types';
import type { SupplierFormData } from './types';
import SupplierNameSearch from '../../suppliers-list/SupplierNameSearch';

interface WizardStep2DetailsProps {
  formData: SupplierFormData;
  onInputChange: (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[]) => void;
  previewMode: boolean;
  customerSites: Site[];
  // CR-6685: existing-supplier detection. Enabled only for brand-new suppliers
  // (editing an existing draft is already tied to a supplier, so there is
  // nothing to detect). When enabled, the Supplier Name field becomes a
  // typeahead — picking a match links the existing supplier instead of
  // creating a duplicate, and its filled-in fields are locked.
  existingSearchEnabled: boolean;
  existingSupplierId: number | null;
  onSelectExisting: (match: ExistingSupplierMatch) => void;
  onSupplierNameChange: (name: string) => void;
  onClearExisting: () => void;
  lockedField: (key: keyof ExistingSupplierMatch) => boolean;
  // CR-6366 alignment: update one linked company's per-site supplier code /
  // currency (written to that company's relationship on create).
  onCustomerSettingChange: (id: number, patch: Partial<{ supplier_code: string; currency: string }>) => void;
}

/**
 * CR-5390: Supplier Details restructure.
 *  - Field order: Supplier Name → structured Address → Currency → Annual Spend → Description.
 *  - Address split into Line 1, Line 2 (optional), City, County/Region (optional),
 *    Postcode (mandatory), Country (optional).
 *  - The previous top-of-form Customer dropdown is removed: every customer
 *    company is added through the Customer Companies section below.
 *
 * CR-5877: Risk Question Routing Management.
 *  - The "first selected = primary customer" auto-assignment is gone — the
 *    buyer now explicitly chooses how risk questionnaires are routed
 *    (Group Level, or Site Level with Single Site / All Sites within group).
 */
export default function WizardStep2Details({
  formData,
  onInputChange,
  previewMode,
  customerSites,
  existingSearchEnabled,
  existingSupplierId,
  onSelectExisting,
  onSupplierNameChange,
  onClearExisting,
  lockedField,
  onCustomerSettingChange,
}: WizardStep2DetailsProps) {
  const linkedIds = formData.customer_ids || [];
  const availableSites = customerSites.filter((s) => !linkedIds.includes(s.id));

  // CR-5877: derive the routing UI's dropdown options from the linked
  // customer sites only — the buyer can only route to companies they've
  // actually selected for this supplier.
  const linkedSites = linkedIds
    .map((sid) => customerSites.find((s) => s.id === sid))
    .filter((s): s is Site => !!s);
  const groupOptions = Array.from(
    new Map(
      linkedSites
        .filter((s) => s.group_id != null && s.group_name)
        .map((s) => [s.group_id as number, s.group_name as string])
    ).entries(),
  );
  const routingLevel = formData.risk_routing_level ?? null;
  const routingScope = formData.risk_routing_site_scope ?? null;

  // When only one choice exists, pick it — forcing the user to open a
  // dropdown containing a single option is pure friction (single-site
  // customers hit this on every supplier they add).
  const onlySiteId = linkedSites.length === 1 ? linkedSites[0].id : null;
  const onlyGroupId = groupOptions.length === 1 ? groupOptions[0][0] : null;
  useEffect(() => {
    if (previewMode) return;
    if (routingLevel === 'site' && routingScope === 'single'
        && !formData.risk_routing_site_id && onlySiteId != null) {
      onInputChange('risk_routing_site_id', onlySiteId);
    }
    if (((routingLevel === 'site' && routingScope === 'all') || routingLevel === 'group')
        && !formData.risk_routing_group_id && onlyGroupId != null) {
      onInputChange('risk_routing_group_id', onlyGroupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routingLevel, routingScope, onlySiteId, onlyGroupId,
      formData.risk_routing_site_id, formData.risk_routing_group_id, previewMode]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Supplier Details</h2>
        <p className="text-muted-foreground">
          Enter the supplier company information. Fields marked with <span className="text-red-500">*</span> are mandatory.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="company_name">Supplier Name <span className="text-red-500">*</span></Label>
          {existingSearchEnabled ? (
            <>
              {/* CR-6685: typeahead over existing suppliers — pick a match to
                  reuse and link it (no duplicate); free text adds a new supplier
                  as before. */}
              <SupplierNameSearch
                value={formData.company_name}
                onChange={onSupplierNameChange}
                onSelectExisting={onSelectExisting}
                placeholder="Start typing to find an existing supplier…"
              />
              <p className="text-xs text-muted-foreground">
                We search your existing suppliers as you type — pick one to reuse and link it, or keep typing to add a new supplier.
              </p>
              {existingSupplierId !== null && (
                <div className="mt-1 flex items-start gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium">Using an existing supplier</p>
                    <p className="text-xs text-muted-foreground">
                      It will be linked to the selected customer companies — no duplicate is created. Its filled-in details are read-only; you can complete any empty fields and they'll be saved to the supplier.
                    </p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={onClearExisting}>Change</Button>
                </div>
              )}
            </>
          ) : (
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => onInputChange('company_name', e.target.value)}
              placeholder="Enter supplier name"
              disabled={previewMode}
              required
            />
          )}
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="address_line_1">Address Line 1 <span className="text-red-500">*</span></Label>
          <Input
            id="address_line_1"
            value={formData.address_line_1 || ''}
            onChange={(e) => onInputChange('address_line_1', e.target.value)}
            placeholder="Street address"
            disabled={previewMode || lockedField('address_line_1')}
            required
          />
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="address_line_2">Address Line 2</Label>
          <Input
            id="address_line_2"
            value={formData.address_line_2 || ''}
            onChange={(e) => onInputChange('address_line_2', e.target.value)}
            placeholder="Apartment, suite, etc. (optional)"
            disabled={previewMode || lockedField('address_line_2')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_city">City / Town</Label>
          <Input
            id="address_city"
            value={formData.address_city || ''}
            onChange={(e) => onInputChange('address_city', e.target.value)}
            placeholder="City or town"
            disabled={previewMode || lockedField('city')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_region">County / Region</Label>
          <Input
            id="address_region"
            value={formData.address_region || ''}
            onChange={(e) => onInputChange('address_region', e.target.value)}
            placeholder="County or region (optional)"
            disabled={previewMode || lockedField('state')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_postcode">Postcode <span className="text-red-500">*</span></Label>
          <Input
            id="address_postcode"
            value={formData.address_postcode || ''}
            onChange={(e) => onInputChange('address_postcode', e.target.value)}
            placeholder="Postcode / ZIP"
            disabled={previewMode || lockedField('postal_code')}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address_country">Country</Label>
          <Input
            id="address_country"
            value={formData.address_country || ''}
            onChange={(e) => onInputChange('address_country', e.target.value)}
            placeholder="Country"
            disabled={previewMode || lockedField('country')}
          />
        </div>

        {/* Company-level email — the supplier organisation's own address, NOT
            the primary contact's. The two used to be conflated: the contact's
            address was promoted onto Company.primary_email, so Supplier Detail
            → Company Overview showed a person where a company belongs. */}
        <div className="col-span-2 space-y-2">
          <Label htmlFor="company_email">Company Email</Label>
          <Input
            id="company_email"
            type="email"
            value={formData.company_email || ''}
            onChange={(e) => onInputChange('company_email', e.target.value)}
            placeholder="e.g. info@supplier.com"
            disabled={previewMode}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency">Currency <span className="text-red-500">*</span></Label>
          <Select
            value={formData.currency}
            onValueChange={(v) => onInputChange('currency', v)}
            disabled={previewMode}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select currency..." />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">(for dashboard conversions)</p>
        </div>

        {/* CR-3943: Annual Spend as dropdown instead of text input */}
        <div className="space-y-2">
          <Label htmlFor="annual_spend">Annual Spend <span className="text-red-500">*</span></Label>
          <Select
            value={formData.annual_spend}
            onValueChange={(v) => onInputChange('annual_spend', v)}
            disabled={previewMode}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select annual spend range..." />
            </SelectTrigger>
            <SelectContent>
              {getAnnualSpendOptions(formData.currency).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Enter description"
            rows={4}
            disabled={previewMode}
          />
        </div>
      </div>

      {/* CR-5390: renamed Additional Companies → Customer Companies.
          CR-5877: removed the "first selection becomes primary" copy and the
          per-row Primary badge — routing is now explicit in the section below. */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Customer Companies <span className="text-red-500">*</span></h3>
            <p className="text-sm text-muted-foreground">Select the customer companies this supplier serves.</p>
          </div>
          {availableSites.length > 0 && (
            <Select
              value=""
              onValueChange={(v) => {
                const siteId = Number(v);
                // CR-6654: never add the same customer company twice — a
                // duplicate customer_ids entry used to 500 the backend save.
                if (linkedIds.includes(siteId)) return;
                const next = [...linkedIds, siteId];
                onInputChange('customer_ids', next);
              }}
              disabled={previewMode}
            >
              <SelectTrigger className="w-[240px]">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  <span>{linkedIds.length === 0 ? 'Add Customer Company' : 'Add Another Company'}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {availableSites.map((site) => (
                  <SelectItem key={site.id} value={site.id.toString()}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {linkedIds.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
            <Building2 className="h-10 w-10 mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-muted-foreground">No customer companies selected yet. Add at least one to continue.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {linkedIds.map((siteId) => {
              const site = customerSites.find((s) => s.id === siteId);
              const cs = formData.customer_settings?.[siteId];
              return (
                <div key={siteId} className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{site?.name || `Site #${siteId}`}</span>
                    {site?.city && (
                      <span className="text-sm text-muted-foreground">— {site.city}{site.country ? `, ${site.country}` : ''}</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const next = linkedIds.filter((id) => id !== siteId);
                      onInputChange('customer_ids', next);
                      // CR-5877: if the removed site backed the current routing
                      // configuration, reset the affected fields so step 2
                      // re-validates rather than silently routing to a site
                      // the supplier no longer serves.
                      if (formData.risk_routing_site_id === siteId) {
                        onInputChange('risk_routing_site_id', null);
                      }
                      const removedGroupId = customerSites.find((s) => s.id === siteId)?.group_id ?? null;
                      const groupStillReachable = next.some((id) => {
                        const s = customerSites.find((cs) => cs.id === id);
                        return s?.group_id === removedGroupId;
                      });
                      if (removedGroupId != null && !groupStillReachable
                          && formData.risk_routing_group_id === removedGroupId) {
                        onInputChange('risk_routing_group_id', null);
                      }
                    }}
                    disabled={previewMode}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                  </div>
                  {/* CR-6366 alignment: this customer's own code + currency,
                      written to its relationship. Currency defaults to the
                      top-level currency and can be overridden per site. */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1 min-w-0 space-y-1">
                      <Label className="text-xs text-muted-foreground">Supplier code (optional)</Label>
                      <Input
                        value={cs?.supplier_code || ''}
                        onChange={(e) => onCustomerSettingChange(siteId, { supplier_code: e.target.value })}
                        placeholder="This customer's code for the supplier"
                        disabled={previewMode}
                      />
                    </div>
                    <div className="space-y-1 sm:w-28">
                      <Label className="text-xs text-muted-foreground">Currency</Label>
                      <select
                        value={cs?.currency || formData.currency}
                        onChange={(e) => onCustomerSettingChange(siteId, { currency: e.target.value })}
                        disabled={previewMode}
                        className="w-full px-3 py-2 border rounded-md text-sm bg-background disabled:opacity-60"
                      >
                        {CURRENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CR-5877: Risk Question Routing Management.
            Buyer explicitly chooses the routing level for risk questionnaires.
            Group  → entire customer group
            Site   → single site, or all sites within a group */}
        {linkedIds.length > 0 && (
          <div className="pt-4 border-t space-y-4">
            <div>
              <h3 className="text-lg font-medium">Risk Question Routing Management <span className="text-red-500">*</span></h3>
              <p className="text-sm text-muted-foreground">How do you want to manage risk question routing?</p>
            </div>

            <RadioGroup
              value={routingLevel ?? ''}
              onValueChange={(v) => {
                const next = (v === 'group' || v === 'site') ? v : null;
                onInputChange('risk_routing_level', next);
                // Reset dependent fields when switching level so stale state
                // from the other branch doesn't leak through validation.
                if (next === 'group') {
                  onInputChange('risk_routing_site_scope', null);
                  onInputChange('risk_routing_site_id', null);
                } else if (next === 'site') {
                  // keep risk_routing_group_id — site routing also tracks group context
                }
              }}
              disabled={previewMode}
              className="gap-3"
            >
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <RadioGroupItem value="group" id="routing-group" className="mt-1" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="routing-group" className="font-medium cursor-pointer">
                    Group Level Routing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Route risk questionnaires to the entire customer group.
                  </p>
                  {routingLevel === 'group' && (
                    <Select
                      value={formData.risk_routing_group_id ? String(formData.risk_routing_group_id) : ''}
                      onValueChange={(v) => onInputChange('risk_routing_group_id', Number(v))}
                      disabled={previewMode}
                    >
                      <SelectTrigger className="w-full max-w-md">
                        <SelectValue placeholder={groupOptions.length === 0 ? 'No groups available for selected companies' : 'Select customer group...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {groupOptions.map(([gid, gname]) => (
                          <SelectItem key={gid} value={String(gid)}>{gname}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <RadioGroupItem value="site" id="routing-site" className="mt-1" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="routing-site" className="font-medium cursor-pointer">
                    Site Level Routing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Target a specific site, or distribute across all sites within a group.
                  </p>
                  {routingLevel === 'site' && (
                    <div className="space-y-2">
                      <RadioGroup
                        value={routingScope ?? ''}
                        onValueChange={(v) => {
                          const next = (v === 'single' || v === 'all') ? v : null;
                          onInputChange('risk_routing_site_scope', next);
                          if (next === 'single') {
                            onInputChange('risk_routing_group_id', null);
                          } else if (next === 'all') {
                            onInputChange('risk_routing_site_id', null);
                          }
                        }}
                        disabled={previewMode}
                        className="gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="single" id="routing-site-single" />
                          <Label htmlFor="routing-site-single" className="cursor-pointer text-sm">Single site</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="all" id="routing-site-all" />
                          <Label htmlFor="routing-site-all" className="cursor-pointer text-sm">All sites within group</Label>
                        </div>
                      </RadioGroup>

                      {routingScope === 'single' && (
                        <Select
                          value={formData.risk_routing_site_id ? String(formData.risk_routing_site_id) : ''}
                          onValueChange={(v) => onInputChange('risk_routing_site_id', Number(v))}
                          disabled={previewMode}
                        >
                          <SelectTrigger className="w-full max-w-md">
                            <SelectValue placeholder="Select site..." />
                          </SelectTrigger>
                          <SelectContent>
                            {linkedSites.map((s) => (
                              <SelectItem key={s.id} value={String(s.id)}>
                                {s.name}
                                {s.group_name ? ` — ${s.group_name}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {routingScope === 'all' && (
                        <Select
                          value={formData.risk_routing_group_id ? String(formData.risk_routing_group_id) : ''}
                          onValueChange={(v) => onInputChange('risk_routing_group_id', Number(v))}
                          disabled={previewMode}
                        >
                          <SelectTrigger className="w-full max-w-md">
                            <SelectValue placeholder={groupOptions.length === 0 ? 'No groups available for selected companies' : 'Select customer group...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {groupOptions.map(([gid, gname]) => (
                              <SelectItem key={gid} value={String(gid)}>{gname}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </RadioGroup>
          </div>
        )}
      </div>
    </div>
  );
}
